import BusinessEntity, { EntityManager, EntityManagerParameters } from '@xFrame4/business/base/BusinessEntity';
import { BusinessEntityFieldType } from '@xFrame4/business/base/Constants';
import { ManyToManyCollection } from '@xFrame4/business/base/ManyToManyCollection';
import { BusinessEntityWithTranslation, BusinessEntityTranslation, EntityWithTranslationManager, EntityTranslationManager, EntityWithTranslationManagerParameters } from '@xFrame4/business/base/BusinessEntityWithTranslation';

import User from '@xFrame4/business/users/User';

export enum PaymentLinkStatus
{
    CREATED = 'CREATED',
    PAYMENT_PENDING = 'PAYMENT_PENDING',
    PAYMENT_FAILED = 'PAYMENT_FAILED',
    PAID = 'PAID',

}

export class PaymentLinkGeneratedEntityManager<B extends PaymentLinkGenerated> extends EntityManager<B>
{

    constructor(parameters: EntityManagerParameters<B>)
    {
        super({
            name: parameters.name ?? 'PaymentLink',
            createEntity: parameters.createEntity,
            fields: [
				{ name: 'dateAdded', type: BusinessEntityFieldType.DateTime, isInput: false },
				{ name: 'status', type: BusinessEntityFieldType.VarChar },
				{ name: 'uniqueLinkId', type: BusinessEntityFieldType.VarChar },
				{ name: 'amount', type: BusinessEntityFieldType.Decimal, isRequiredInput: true },
				{ name: 'currency', type: BusinessEntityFieldType.VarChar, isRequiredInput: true },
				{ name: 'language', type: BusinessEntityFieldType.VarChar, isRequiredInput: true },
				{ name: 'description', type: BusinessEntityFieldType.Text },
				{ name: 'admin', type: BusinessEntityFieldType.ForeignKey, inputName: 'adminId', inputProperty: 'admin.id', relatedManager: User.manager },
				{ name: 'dateModified', type: BusinessEntityFieldType.DateTime },
				{ name: 'paymentGatewayCode', type: BusinessEntityFieldType.VarChar },
				{ name: 'paymentGatewayTransactionId', type: BusinessEntityFieldType.VarChar },
				{ name: 'paymentGatewayMessage', type: BusinessEntityFieldType.Text },
				{ name: 'customerEmail', type: BusinessEntityFieldType.VarChar, isRequiredInput: true },
				{ name: 'customerName', type: BusinessEntityFieldType.VarChar },
				{ name: 'customerPhone', type: BusinessEntityFieldType.VarChar },
				{ name: 'expiresAt', type: BusinessEntityFieldType.DateTime },
				{ name: 'notes', type: BusinessEntityFieldType.Text },
                ... parameters.fields ?? [],
            ],
            graphQlQueryAlias: parameters.graphQlQueryAlias ?? 'paymentLinks',
        });
    }

}
/**
 * Represents a shareable payment link that can be sent to customers for making direct payments to the hotel. Payment links can be created by admin users and shared via email, SMS, or any other communication channel. Each link has a unique identifier and tracks payment status through the Netopia payment gateway.
 */
export class PaymentLinkGenerated extends BusinessEntity
{
	/** The date and time when the payment link was created */
	dateAdded!: string;
	/** The current status of the payment link */
	status: PaymentLinkStatus = PaymentLinkStatus.CREATED;
	/** Unique identifier for the payment link URL */
	uniqueLinkId!: string;
	/** The payment amount */
	amount!: number;
	/** Currency code (RON, EUR, USD) */
	currency!: string;
	/** Language code for payment page (ro, hu, en, de) */
	language!: string;
	/** Description of the payment shown to customer */
	description: string = '';
	/** The admin user who created the link */
	admin!: User;
	/** Last modification date */
	dateModified: string | null = null;
	/** Payment gateway used (netopia) */
	paymentGatewayCode: string | null = null;
	/** Transaction ID from gateway */
	paymentGatewayTransactionId: string | null = null;
	/** Message/status from payment gateway */
	paymentGatewayMessage: string | null = null;
	/** Customer email for pre-filling payment form */
	customerEmail: string = '';
	/** Customer name for pre-filling payment form */
	customerName: string = '';
	/** Customer phone for pre-filling payment form */
	customerPhone: string = '';
	/** Expiration date for the payment link */
	expiresAt: string | null = null;
	/** Internal notes about the payment link */
	notes: string = '';

}

export default PaymentLinkGenerated;